// src/pages/BiologicalPage.js
import React, { useEffect, useRef, useState } from "react";
import "./BiologicalPage.css";
import { useLocation, useNavigate } from "react-router-dom";
import { getApiBase } from "../api/healthThreatsApi";
//import rison from "rison";

function toApiCountry(uiCountry) {
  return (uiCountry || "").trim().toUpperCase();
}

const TB_COUNTRY_ISO = {
  SPAIN: "ES",
  GREECE: "GR",
  ITALY: "IT",
  ISRAEL: "IL",
  UKRAINE: "UA",
  IRELAND: "IE",
  SWEDEN: "SE",
  UK: "GB",
};

function toTbCountryIso(apiCountry) {
  const iso = TB_COUNTRY_ISO[(apiCountry || "").trim().toUpperCase()];
  if (!iso) throw new Error(`No TB ISO mapping is available for ${apiCountry}.`);
  return iso;
}

function toApiDiseaseToken(uiDisease) {
  // UI values
  if (uiDisease === "Tuberculosis") return "TB";
  if (uiDisease === "DiseaseX") return "DiseaseX";
  if (uiDisease === "Plague") return "Plague";
  return (uiDisease || "").trim();
}

const API_BASE = getApiBase();

// set to true only if the legacy "Local dataset" / "IDL" radio selector must be restored.
const ENABLE_LEGACY_LOCAL_TRAINING = false;

const READY_TRAINING_JOB_ENDPOINTS = {
  Plague: {
    backendEndpoint: "/train/jobs/plague/idl",
    available: true,
  },
  TB: {
    backendEndpoint: "/train/jobs/tb/idl",
    available: true,
  },
  DiseaseX: {
    backendEndpoint: "/train/jobs/diseasex/idl",
    available: true,
  },
};

function getReadyTrainingJobEndpoint(apiDisease) {
  return (
    READY_TRAINING_JOB_ENDPOINTS[apiDisease] || {
      backendEndpoint: null,
      available: false,
    }
  );
}

const SUPPORTED_DATASET_COMBINATIONS = {
  Plague: new Set(["SWITZERLAND"]),
  //TB: new Set(["GREECE", "UKRAINE"]),
  TB: new Set(["GREECE", "UKRAINE", "ITALY", "IRELAND", "SWEDEN", "ISRAEL", "SPAIN"]),
  DiseaseX: new Set(["UK"]),
};

function isSupportedDatasetCombination(apiCountry, apiDisease) {
  return Boolean(SUPPORTED_DATASET_COMBINATIONS[apiDisease]?.has(apiCountry));
}

function getUnsupportedDatasetMessage(apiCountry, apiDisease) {
  return `No training dataset is available for ${apiCountry} + ${apiDisease}.`;
}

const TRAINING_JOB_STORAGE_KEY = "biological_active_training_job";
const LAST_TRAINING_RESULT_STORAGE_KEY = "biological_last_training_result";

const HISTORICAL_DASHBOARD_URLS = {
  plague: process.env.REACT_APP_HISTORICAL_PLAGUE_DASHBOARD_URL || "",
  tuberculosis: process.env.REACT_APP_HISTORICAL_TB_DASHBOARD_URL || "",
  tb: process.env.REACT_APP_HISTORICAL_TB_DASHBOARD_URL || "",
  diseasex: process.env.REACT_APP_HISTORICAL_DISEASEX_DASHBOARD_URL || "",
  "disease x": process.env.REACT_APP_HISTORICAL_DISEASEX_DASHBOARD_URL || "",
};

const HISTORICAL_DISEASE_OPTIONS = ["Plague", "Tuberculosis", "Disease X"];

function normalizeDiseaseName(value) {
  return (value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function getHistoricalDashboardUrl(disease) {
  return HISTORICAL_DASHBOARD_URLS[normalizeDiseaseName(disease)] || "";
}

function hasHistoricalData(disease) {
  return Boolean(getHistoricalDashboardUrl(disease));
}

async function apiErrorToMessage(res) {
  const fallback = `Request failed (${res.status})`;
  try {
    const ct = res.headers.get("content-type") || "";

    if (ct.includes("application/json")) {
      const j = await res.json().catch(() => null);
      if (!j) return fallback;

      const d = j.detail ?? j.message ?? j.error ?? j;
      return typeof d === "string" ? d : JSON.stringify(d, null, 2);
    }

    const t = await res.text().catch(() => "");
    return t?.trim() ? t.trim() : fallback;
  } catch {
    return fallback;
  }
}

function _isPredictLike(nameLower) {
  return (
    nameLower.includes("input") ||
    nameLower.includes("predict") ||
    nameLower.includes("test")
  );
}

function _onlyCsv(files) {
  return files.filter((f) => String(f).toLowerCase().endsWith(".csv"));
}

function _pickPlagueTrain(files) {
  const wanted = "plague_data_by_date.csv";
  const exact = files.find((f) => String(f).toLowerCase() === wanted);
  if (exact) return [exact];
  const alt = files.find((f) =>
    String(f).toLowerCase().includes("plague_data_by_date")
  );
  return alt ? [alt] : [];
}

function _pickTbTrain(files, apiCountry) {
  const c = String(apiCountry || "").toLowerCase();
  return files.filter((f) => {
    const n = String(f).toLowerCase();
    if (_isPredictLike(n)) return false;
    if (!n.includes(c)) return false;
    if (!n.includes("tb")) return false;
    return true;
  });
}

function _pickDiseaseXTrain(files, apiCountry) {
  const c = String(apiCountry || "").toLowerCase();
  return files.filter((f) => {
    const n = String(f).toLowerCase();
    if (_isPredictLike(n)) return false;

    const hasKnownKeyword =
      n.includes("gtrends") || n.includes("epd") || n.includes("mobility");
    if (!hasKnownKeyword) return false;

    return true;
  });
}

function _filterByKeyword(files, keyword) {
  const k = String(keyword).toLowerCase();
  return files.filter((f) => String(f).toLowerCase().includes(k));
}

function _pickTbInput(files, apiCountry) {
  const c = String(apiCountry || "").toLowerCase();
  return files.filter((f) => {
    const n = String(f).toLowerCase();
    if (!n.includes("input")) return false;
    if (!n.includes("tb")) return false;
    if (c && !n.includes(c)) return false;
    return true;
  });
}

function _pickPlagueInput(files) {
  return files.filter((f) => {
    const n = String(f).toLowerCase();
    return n.includes("input") && n.includes("plague");
  });
}

function _pickDiseaseXInput(files) {
  return files.filter((f) => {
    const n = String(f).toLowerCase();
    if (!n.includes("input")) return false;
    const hasKnownKeyword =
      n.includes("gtrends") || n.includes("epd") || n.includes("mobility");
    return hasKnownKeyword;
  });
}

function normalizeInfoKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\.(html|json)$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const VISUALIZATION_INFO_MESSAGES = {
  MESSAGE_1: "Message 1",
  MESSAGE_2: "Message 2",
  MESSAGE_3: "Message 3",
  MESSAGE_4: "Message 4",
  MESSAGE_5: "Message 5",
  MESSAGE_6: "Message 6",
  MESSAGE_7: "An interactive time-series chart illustrating the fluctuations in the risk factor inferred by the AI model upon data collected for the UK during 2020. The instances at which the system identifies an irregularity (to any of the indicators of concern) are indicated by purple circles. The magenta line indicates the normalized number of new cases, while the dark magenta line refers to the normalized number of deaths. The vertical dashed gray line corresponds to the date of the pandemic announcement by WHO. The purple shaded rectangles relate to periods when a lockdown has been imposed at the national level, while the light gray area indicates the time period during which mobility data is missing.",
  MESSAGE_8: "An interactive time-series chart illustrating the evolution of search trends for health-related terms in UK during 2020. The irregularities inferred by the AI ​​model are highlighted with red circles. Interaction with these circles provides insights regarding the features that contribute to each irregularity.",
  MESSAGE_9: "An interactive time-series chart illustrating the evolution of prescription costs issued in the UK during 2020. The irregularities inferred by the AI ​​model are highlighted with red circles. Interaction with these circles provides insights regarding the features that contribute to each irregularity.",
  MESSAGE_10: "An interactive time-series chart illustrating the evolution of mobility patterns in the UK during 2020. The irregularities inferred by the AI ​​model are highlighted with red circles. Interaction with these circles provides insights regarding the features that contribute to each irregularity.",
  MESSAGE_11: "An interactive time-series chart illustrating the observed pneumonic plague cases over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink dashed line).",
  MESSAGE_12: "An interactive geospatial map illustrating the geographic and temporal distribution of observed and predicted pneumonic plague cases. The provided heatmaps highlight the hotspot areas, while information about the cases sex and age group are also available.",

  GREECE_MESSAGE_1: "An interactive geospatial map illustrating the geographic and temporal distribution of observed and predicted TB cases, along with RR-TB cases (annotated with larger circles). The provided heatmaps highlight the hotspot areas, while information about the cases sex and age group are also available.",
  GREECE_MESSAGE_2: "An interactive time-series chart illustrating the observed number of deaths caused by TB over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink thick dashed line). The upper and lower bounds of the predictions are also provided (marked with the pink thin dashed lines).",
  GREECE_MESSAGE_3: "An interactive time-series chart illustrating the observed TB incidence over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink thick dashed line). The upper and lower bounds of the predictions are also provided (marked with the pink thin dashed lines).",
  GREECE_MESSAGE_4: "An interactive time-series chart illustrating the reported new and relapse TB cases notified over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink thick dashed line).",
  GREECE_MESSAGE_5: "An interactive time-series chart illustrating the reported number of TB cases attributable to five risk factors over recent observation period (denoted by continuous lines), along with the AI model forecasts (marked with the dashed lines).",
  GREECE_MESSAGE_6: "Message 6 - Greece",

  UKRAINE_MESSAGE_1: "An interactive geospatial map illustrating the geographic and temporal distribution of observed and predicted TB cases, along with RR-TB cases (annotated with larger circles). The provided heatmaps highlight the hotspot areas, while information about the cases sex and age group are also available.",
  UKRAINE_MESSAGE_2: "An interactive time-series chart illustrating the observed number of deaths caused by TB over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink thick dashed line). The upper and lower bounds of the predictions are also provided (marked with the pink thin dashed lines).",
  UKRAINE_MESSAGE_3: "An interactive time-series chart illustrating the observed TB incidence over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink thick dashed line). The upper and lower bounds of the predictions are also provided (marked with the pink thin dashed lines).",
  UKRAINE_MESSAGE_4: "An interactive time-series chart illustrating the reported new and relapse TB cases notified over recent observation period (denoted by blue line), along with the AI model forecasts (marked with the pink thick dashed line).",
  UKRAINE_MESSAGE_5: "An interactive time-series chart illustrating the reported number of TB cases attributable to five risk factors over recent observation period (denoted by continuous lines), along with the AI model forecasts (marked with the dashed lines).",
  UKRAINE_MESSAGE_6: "Message 6 - Ukraine",
};

const VISUALIZATION_INFO_MESSAGE_ALIASES = {
  // Message 1 — TB heat map
  "heat map tb folium": "MESSAGE_1",
  "heatmap tb folium": "MESSAGE_1",
  "tb heat map folium": "MESSAGE_1",
  "tb heatmap folium": "MESSAGE_1",

  // Message 2 — TB deaths
  "estimated tb deaths predictions plotly": "MESSAGE_2",
  "tb deaths predictions plotly": "MESSAGE_2",
  "estimated deaths tb predictions plotly": "MESSAGE_2",

  // Message 3 — TB incidence
  "estimated tb incidence predictions plotly": "MESSAGE_3",
  "tb incidence predictions plotly": "MESSAGE_3",
  "estimated incidence tb predictions plotly": "MESSAGE_3",

  // Message 4 — New and relapse TB cases
  "new n relapse tb cases predictions plotly": "MESSAGE_4",
  "new and relapse tb cases predictions plotly": "MESSAGE_4",
  "new relapse tb cases predictions plotly": "MESSAGE_4",
  "tb new n relapse cases predictions plotly": "MESSAGE_4",

  // Message 5 — TB risk factors
  "risk factors predictions tb plotly": "MESSAGE_5",
  "risk factor predictions tb plotly": "MESSAGE_5",
  "tb risk factors predictions plotly": "MESSAGE_5",

  // Message 6 — RR-TB cases
  "estimated rr tb cases predictions plotly": "MESSAGE_6",
  "rr tb cases predictions plotly": "MESSAGE_6",
  "estimated rrtb cases predictions plotly": "MESSAGE_6",

  // Message 7 — Disease X risk factor
  "risk factor diseasex plotly": "MESSAGE_7",
  "risk factor disease x plotly": "MESSAGE_7",
  "diseasex risk factor plotly": "MESSAGE_7",
  "disease x risk factor plotly": "MESSAGE_7",

  // Message 8 — Disease X Google Trends irregularities
  "google trends irregularities diseasex plotly": "MESSAGE_8",
  "google trends irregularities disease x plotly": "MESSAGE_8",
  "diseasex google trends irregularities plotly": "MESSAGE_8",

  // Message 9 — Disease X pharmacy prescribing irregularities
  "pharmacy prescribing irregularities diseasex plotly": "MESSAGE_9",
  "pharmacy prescribing irregularities disease x plotly": "MESSAGE_9",
  "diseasex pharmacy prescribing irregularities plotly": "MESSAGE_9",

  // Message 10 — Disease X mobility irregularities
  "mobility irregularities diseasex plotly": "MESSAGE_10",
  "mobility irregularities disease x plotly": "MESSAGE_10",
  "diseasex mobility irregularities plotly": "MESSAGE_10",

  // Message 11 — Plague predictions
  "plague predictions plotly": "MESSAGE_11",
  "pneumonic plague predictions plotly": "MESSAGE_11",
  "plague prediction plotly": "MESSAGE_11",

  // Message 12 — Plague heat map
  "heatmap plague folium": "MESSAGE_12",
  "heat map plague folium": "MESSAGE_12",
  "plague heatmap folium": "MESSAGE_12",
  "plague heat map folium": "MESSAGE_12",
};

function getVisualizationCountryKey(asset, selectedCountry) {
  const combinedKey = `${normalizeInfoKey(asset?.title)} ${normalizeInfoKey(
    asset?.filename
  )} ${normalizeInfoKey(selectedCountry)}`;

  if (combinedKey.includes("greece")) return "GREECE";
  if (combinedKey.includes("ukraine")) return "UKRAINE";
  if (combinedKey.includes("italy")) return "ITALY";
  if (combinedKey.includes("ireland")) return "IRELAND";
  if (combinedKey.includes("sweden")) return "SWEDEN";
  if (combinedKey.includes("israel")) return "ISRAEL";

  return null;
}

function getVisualizationInfoMessageId(asset) {
  const titleKey = normalizeInfoKey(asset?.title);
  const filenameKey = normalizeInfoKey(asset?.filename);

  const exactMatch =
    VISUALIZATION_INFO_MESSAGE_ALIASES[titleKey] ||
    VISUALIZATION_INFO_MESSAGE_ALIASES[filenameKey];

  if (exactMatch) {
    return exactMatch;
  }

  const combinedKey = `${titleKey} ${filenameKey}`.trim();

  if (!combinedKey) {
    return null;
  }

  const isTb =
    combinedKey.includes("tb") ||
    combinedKey.includes("tuberculosis");

  const isDiseaseX =
    combinedKey.includes("diseasex") ||
    combinedKey.includes("disease x");

  const isPlague = combinedKey.includes("plague");

  if (isTb) {
    if (
      combinedKey.includes("heatmap") ||
      combinedKey.includes("heat map") ||
      combinedKey.includes("folium")
    ) {
      return "MESSAGE_1";
    }

    if (combinedKey.includes("death")) {
      return "MESSAGE_2";
    }

    if (combinedKey.includes("incidence")) {
      return "MESSAGE_3";
    }

    if (
      combinedKey.includes("new") &&
      combinedKey.includes("relapse")
    ) {
      return "MESSAGE_4";
    }

    if (
      combinedKey.includes("rr tb") ||
      combinedKey.includes("rrtb") ||
      combinedKey.includes("rifampicin")
    ) {
      return "MESSAGE_6";
    }

    if (combinedKey.includes("risk")) {
      return "MESSAGE_5";
    }
  }

  if (isDiseaseX) {
    if (combinedKey.includes("google") || combinedKey.includes("gtrends")) {
      return "MESSAGE_8";
    }

    if (
      combinedKey.includes("pharmacy") ||
      combinedKey.includes("prescribing") ||
      combinedKey.includes("epd")
    ) {
      return "MESSAGE_9";
    }

    if (combinedKey.includes("mobility")) {
      return "MESSAGE_10";
    }

    if (combinedKey.includes("risk")) {
      return "MESSAGE_7";
    }
  }

  if (isPlague) {
    if (
      combinedKey.includes("heatmap") ||
      combinedKey.includes("heat map") ||
      combinedKey.includes("folium")
    ) {
      return "MESSAGE_12";
    }

    if (
      combinedKey.includes("prediction") ||
      combinedKey.includes("predictions") ||
      combinedKey.includes("plotly")
    ) {
      return "MESSAGE_11";
    }
  }

  return null;
}

function getVisualizationInfoMessage(asset, selectedCountry) {
  const messageId = getVisualizationInfoMessageId(asset);

  if (messageId) {
    const countryKey = getVisualizationCountryKey(asset, selectedCountry);
    const countrySpecificMessageId = countryKey
      ? `${countryKey}_${messageId}`
      : null;

    if (
      countrySpecificMessageId &&
      VISUALIZATION_INFO_MESSAGES[countrySpecificMessageId]
    ) {
      return VISUALIZATION_INFO_MESSAGES[countrySpecificMessageId];
    }

    if (VISUALIZATION_INFO_MESSAGES[messageId]) {
      return VISUALIZATION_INFO_MESSAGES[messageId];
    }
  }

  const titleKey = normalizeInfoKey(asset?.title);
  const filenameKey = normalizeInfoKey(asset?.filename);
  const debugName = titleKey || filenameKey || "unknown visualization";

  return `No  information for this visualization: ${debugName}`;
}

function InfoTooltip({ message }) {
  if (!message) return null;

  return (
    <span className="bp-info-wrap">
      <button
        type="button"
        className="bp-info-button"
        aria-label="Show information"
      >
        i
      </button>

      <span className="bp-info-tooltip" role="tooltip">
        {message}
      </span>
    </span>
  );
}

export default function BiologicalPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [country, setCountry] = useState("UK");
  const [disease, setDisease] = useState("DiseaseX");

  // visualization mode: null | "chart" | "map"
  const [vizMode, setVizMode] = useState(null);

  // dropdown state for Visualization menu
  const [vizMenuOpen, setVizMenuOpen] = useState(false);
  const vizWrapRef = useRef(null);

  const [historicalMenuOpen, setHistoricalMenuOpen] = useState(false);
  const historicalWrapRef = useRef(null);
  const [historicalDisease, setHistoricalDisease] = useState("Plague");

  // ---- Create New AI Models menu (sub-menu) ----
  const [trainMenuOpen, setTrainMenuOpen] = useState(false);
  const trainWrapRef = useRef(null);

  // training mode panel
  const [trainMode, setTrainMode] = useState(null);
  const [trainStatus, setTrainStatus] = useState(null); // null | "loading" | "success" | "error"
  const [trainMsg, setTrainMsg] = useState(null);
  const [plagueTrainSource, setPlagueTrainSource] = useState("idl");

  const [trainingJobId, setTrainingJobId] = useState(null);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const trainingPollRef = useRef(null);

  const saveActiveTrainingJob = (job) => {
    try {
      sessionStorage.setItem(TRAINING_JOB_STORAGE_KEY, JSON.stringify(job));
    } catch {}
  };

  const clearActiveTrainingJob = () => {
    try {
      sessionStorage.removeItem(TRAINING_JOB_STORAGE_KEY);
    } catch {}
  };

  const loadActiveTrainingJob = () => {
    try {
      const raw = sessionStorage.getItem(TRAINING_JOB_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const saveLastTrainingResult = (result) => {
    try {
      sessionStorage.setItem(
        LAST_TRAINING_RESULT_STORAGE_KEY,
        JSON.stringify(result)
      );
    } catch {}
  };

  const loadLastTrainingResult = () => {
    try {
      const raw = sessionStorage.getItem(LAST_TRAINING_RESULT_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const clearLastTrainingResult = () => {
    try {
      sessionStorage.removeItem(LAST_TRAINING_RESULT_STORAGE_KEY);
    } catch {}
  };

  // ready datasets
  const [readyFiles, setReadyFiles] = useState([]);

  // selected ready datasets
  const [selectedTrainFile, setSelectedTrainFile] = useState("");
  const [selectedGtrends, setSelectedGtrends] = useState("");
  const [selectedEpd, setSelectedEpd] = useState("");
  const [selectedMobility, setSelectedMobility] = useState("");

  // upload datasets
  const [uploadTrainFile, setUploadTrainFile] = useState(null);
  const [uploadTbDemographics, setUploadTbDemographics] = useState(null);
  const [uploadTbRiskFactors, setUploadTbRiskFactors] = useState(null);
  const [uploadGtrends, setUploadGtrends] = useState(null);
  const [uploadEpd, setUploadEpd] = useState(null);
  const [uploadMobility, setUploadMobility] = useState(null);

  const [canAdoptPredict, setCanAdoptPredict] = useState(false);
  const predictionAvailabilityRequestRef = useRef(0);

  // assets
  const [assets, setAssets] = useState([]);
  const [assetsError, setAssetsError] = useState(null);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const apiCountry = toApiCountry(country);
  const apiDisease = toApiDiseaseToken(disease);
  const supportedDatasetCombination = isSupportedDatasetCombination(
    apiCountry,
    apiDisease
  );
  const unsupportedDatasetMessage = getUnsupportedDatasetMessage(
    apiCountry,
    apiDisease
  );

  const trainingDiseaseLabel =
    apiDisease === "TB"
      ? "Tuberculosis"
      : apiDisease === "DiseaseX"
      ? "Disease X"
      : apiDisease;

  const trainingDataSourceMessage = `Training data for ${trainingDiseaseLabel} in ${apiCountry} are fetched automatically from the central data repository (IDL).`;

  const historicalDashboardUrl = getHistoricalDashboardUrl(historicalDisease);

  const showHistoricalIframe =
    vizMode === "historical" &&
    hasHistoricalData(historicalDisease) &&
    !!historicalDashboardUrl;
  const showNoHistoricalDataMessage =
    vizMode === "historical" && !showHistoricalIframe;

  const refreshPredictionAvailability = async (
    countryArg = apiCountry,
    diseaseArg = apiDisease
  ) => {
    const requestId = predictionAvailabilityRequestRef.current + 1;
    predictionAvailabilityRequestRef.current = requestId;
    const lastResult = loadLastTrainingResult();

    if (
      lastResult &&
      lastResult.status === "completed" &&
      lastResult.country === countryArg &&
      lastResult.disease === diseaseArg
    ) {
      setCanAdoptPredict(true);
      return;
    }

    setCanAdoptPredict(false);

    if (!countryArg || !diseaseArg) return;

    try {
      const params = new URLSearchParams({
        country: countryArg,
        threat: diseaseArg,
      });
      const response = await fetch(`${API_BASE}/models/exist?${params}`);

      if (!response.ok) return;

      const availability = await response.json();
      if (predictionAvailabilityRequestRef.current === requestId) {
        setCanAdoptPredict(Boolean(availability?.ready_for_prediction));
      }
    } catch {
      // The prediction action remains disabled when availability cannot be checked.
    }
  };

  // Training UX 
  const trainAbortRef = useRef(null);

  const cancelTraining = () => {
    if (trainAbortRef.current) {
      try {
        trainAbortRef.current.abort();
      } catch {}
    }

    if (trainingPollRef.current) {
      clearInterval(trainingPollRef.current);
      trainingPollRef.current = null;
    }
  };

const requestTrainingCancel = async () => {
  const jobId = trainingJobId || loadActiveTrainingJob()?.jobId;
  if (!jobId) return;

  try {
    const res = await fetch(`${API_BASE}/train/jobs/${jobId}/cancel`, {
      method: "POST",
    });

    if (!res.ok) throw new Error(await apiErrorToMessage(res));

    setTrainStatus("loading");
    setTrainMsg("Cancellation requested...");
  } catch (e) {
    setTrainStatus("error");
    setTrainMsg(String(e?.message || e || "Failed to cancel training."));
  }
};

  // =============================
  // Predict (Button 3) UX
  // =============================

  const [predictMenuOpen, setPredictMenuOpen] = useState(false);
  const predictWrapRef = useRef(null);

  const [predictMode, setPredictMode] = useState(null); // null | "ready" | "upload"
  const [predictStatus, setPredictStatus] = useState(null); // null | "loading" | "success" | "error"
  const [predictMsg, setPredictMsg] = useState(null);

  const [readyPredictFiles, setReadyPredictFiles] = useState([]);

  const [selectedPredictFile, setSelectedPredictFile] = useState(""); // TB/Plague
  const [selectedPredictGtrends, setSelectedPredictGtrends] = useState(""); // DiseaseX (optional)
  const [selectedPredictEpd, setSelectedPredictEpd] = useState("");
  const [selectedPredictMobility, setSelectedPredictMobility] = useState("");

  const [uploadPredictFile, setUploadPredictFile] = useState(null);
  const [uploadPredictGtrends, setUploadPredictGtrends] = useState(null);
  const [uploadPredictEpd, setUploadPredictEpd] = useState(null);
  const [uploadPredictMobility, setUploadPredictMobility] = useState(null);

  const predictAbortRef = useRef(null);

  const cancelPredict = () => {
    if (predictAbortRef.current) {
      try {
        predictAbortRef.current.abort();
      } catch {}
    }
  };

  const hardResetAllAiUi = () => {
    // Cancel in-flight requests
    cancelTraining();
    cancelPredict();

    // menus
    setTrainMenuOpen(false);
    setPredictMenuOpen(false);

    // training panel
    setTrainMode(null);
    setTrainStatus(null);
    setTrainMsg(null);
    setReadyFiles([]);

    setSelectedTrainFile("");
    setSelectedGtrends("");
    setSelectedEpd("");
    setSelectedMobility("");

    setUploadTrainFile(null);
    setUploadTbDemographics(null);
    setUploadTbRiskFactors(null);
    setUploadGtrends(null);
    setUploadEpd(null);
    setUploadMobility(null);

    // predict panel
    setPredictMode(null);
    setPredictStatus(null);
    setPredictMsg(null);
    setReadyPredictFiles([]);

    setSelectedPredictFile("");
    setSelectedPredictGtrends("");
    setSelectedPredictEpd("");
    setSelectedPredictMobility("");

    setUploadPredictFile(null);
    setUploadPredictGtrends(null);
    setUploadPredictEpd(null);
    setUploadPredictMobility(null);
  };

  // ---- URL params helpers ----
  const setUrlParams = (next) => {
    const sp = new URLSearchParams(location.search);

    if (typeof next.country !== "undefined") {
      if (next.country) sp.set("country", next.country);
      else sp.delete("country");
    }
    if (typeof next.disease !== "undefined") {
      if (next.disease) sp.set("disease", next.disease);
      else sp.delete("disease");
    }

    if (typeof next.view !== "undefined") {
      if (
        next.view === "map" ||
        next.view === "chart" ||
        next.view === "predictions" ||
        next.view === "historical"
      ) {
        sp.set("view", next.view);
      } else {
        sp.delete("view");
      }
    }

    const search = sp.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true }
    );
  };

  // -----------------------------
  // Visualization (Button 1)
  // -----------------------------
  const fetchVisualizations = async (mode /* "map" | "chart" */) => {
    setAssetsLoading(true);
    setAssetsError(null);
    setAssets([]);

    try {
      const url =
        `${API_BASE}/visualizations/biological` +
        `?country=${encodeURIComponent(apiCountry)}` +
        `&disease=${encodeURIComponent(apiDisease)}` +
        `&prefer=html`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(await apiErrorToMessage(res));

      const data = await res.json();
      const all = Array.isArray(data.assets) ? data.assets : [];

      const filtered = all.filter((a) => {
        if (!a || !a.kind) return false;
        return mode === "map" ? a.kind === "map" : a.kind === "chart";
      });

      setAssets(filtered);
    } catch (e) {
      setAssetsError(String(e?.message || e || "Failed to load visualizations."));
    } finally {
      setAssetsLoading(false);
    }
  };

  const selectVizMode = (mode) => {
    setVizMode(mode); // "map" | "chart"
    setVizMenuOpen(false);
    setUrlParams({ country, disease, view: mode });
    fetchVisualizations(mode);
  };

  // -----------------------------
  // Predictions display button3
  // -----------------------------
  const fetchPredictionsAssets = async () => {
    setAssetsLoading(true);
    setAssetsError(null);
    setAssets([]);

    try {
      const url =
        `${API_BASE}/visualizations/biological` +
        `?country=${encodeURIComponent(apiCountry)}` +
        `&disease=${encodeURIComponent(apiDisease)}` +
        `&prefer=html`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(await apiErrorToMessage(res));

      const data = await res.json();
      const all = Array.isArray(data.assets) ? data.assets : [];

      const filtered = all.filter((a) => a && (a.kind === "map" || a.kind === "chart"));
      setAssets(filtered);
    } catch (e) {
      setAssetsError(String(e?.message || e || "Failed to load prediction outputs."));
    } finally {
      setAssetsLoading(false);
    }
  };

  const openPredictionsView = () => {
    setVizMode("predictions");
    setVizMenuOpen(false);
    setUrlParams({ country, disease, view: "predictions" });
    fetchPredictionsAssets();
  };

  const openHistoricalView = (selectedHistoricalDisease) => {
    setHistoricalDisease(selectedHistoricalDisease);
    setHistoricalMenuOpen(false);
    setVizMode("historical");
    setVizMenuOpen(false);
    setTrainMenuOpen(false);
    setPredictMenuOpen(false);
    setAssets([]);
    setAssetsError(null);
    setAssetsLoading(false);
    setUrlParams({ country, disease, view: "historical" });
  };

  // -----------------------------
  // Training (Button 2)
  // -----------------------------
  const fetchReadyDatasets = async () => {
    if (!supportedDatasetCombination) {
      return [];
    }

    const source = ENABLE_LEGACY_LOCAL_TRAINING ? "local" : "idl";
    const params = new URLSearchParams({
      purpose: "train",
      source,
      country: apiCountry,
      threat: apiDisease,
    });

    const res = await fetch(`${API_BASE}/datasets?${params.toString()}`);
    if (!res.ok) throw new Error(await apiErrorToMessage(res));
    const data = await res.json();

    const filesRaw = Array.isArray(data.files) ? data.files : [];
    const files = _onlyCsv(filesRaw);

    if (apiDisease === "Plague") return _pickPlagueTrain(files);
    if (apiDisease === "TB") return _pickTbTrain(files, apiCountry);
    if (apiDisease === "DiseaseX") return _pickDiseaseXTrain(files, apiCountry);

    return [];
  };

  const openTrainMenu = () => setTrainMenuOpen((v) => !v);
  const startTrainModeReady = async () => {
    setTrainMenuOpen(false);
    setTrainMode("ready");
    setTrainStatus(null);
    setTrainMsg(null);

    setReadyFiles([]);
    setSelectedTrainFile("");
    setSelectedGtrends("");
    setSelectedEpd("");
    setSelectedMobility("");

    if (!supportedDatasetCombination) {
      setTrainStatus("error");
      setTrainMsg(unsupportedDatasetMessage);
      return;
    }

    if (!ENABLE_LEGACY_LOCAL_TRAINING && (apiDisease === "Plague" || apiDisease === "TB" || apiDisease === "DiseaseX")) 
      {
        return;
      }

    try {
      const files = await fetchReadyDatasets();
      setReadyFiles(files);

      if (apiDisease === "Plague" || apiDisease === "TB") {
        setSelectedTrainFile(files[0] || "");
      }

      if (apiDisease === "DiseaseX") {
        const g = _filterByKeyword(files, "gtrends")[0] || "";
        const e = _filterByKeyword(files, "epd")[0] || "";
        const m = _filterByKeyword(files, "mobility")[0] || "";
        setSelectedGtrends(g);
        setSelectedEpd(e);
        setSelectedMobility(m);
      }
    } catch (e) {
      setTrainStatus("error");
      setTrainMsg(String(e?.message || e || "Failed to load ready datasets."));
    }
  };

  const startTrainModeUpload = () => {
    setTrainMenuOpen(false);
    setTrainMode("upload");
    setTrainStatus(null);
    setTrainMsg(null);

    setUploadTrainFile(null);
    setUploadTbDemographics(null);
    setUploadTbRiskFactors(null);
    setUploadGtrends(null);
    setUploadEpd(null);
    setUploadMobility(null);

    if (!supportedDatasetCombination) {
      setTrainStatus("error");
      setTrainMsg(unsupportedDatasetMessage);
    }
  };

  const readCsvFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read file."));
      reader.readAsText(file);
    });

  const csvToObjects = (csvText) => {
    const lines = String(csvText || "")
      .split(/\r?\n/)
      .filter((l) => l.trim().length > 0);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cols = line.split(",").map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => {
        obj[h] = cols[i] ?? "";
      });
      return obj;
    });
  };

  const buildCurrentTrainingMetadata = () => ({
    datasetId:
      trainMode === "ready"
        ? apiDisease === "DiseaseX"
          ? `${selectedGtrends || ""}|${selectedEpd || ""}|${selectedMobility || ""}`
          : selectedTrainFile || null
        : apiDisease === "DiseaseX"
        ? `${uploadGtrends?.name || ""}|${uploadEpd?.name || ""}|${uploadMobility?.name || ""}`
        : apiDisease === "TB"
        ? `${uploadTrainFile?.name || ""}|${uploadTbDemographics?.name || ""}|${uploadTbRiskFactors?.name || ""}`
        : uploadTrainFile?.name || null,
    datasetSource: trainMode || null,
    trainingSource: apiDisease === "Plague" ? plagueTrainSource : "local",
  });

  const startTrainingPolling = (jobId) => {
    if (!jobId) return;

    if (trainingPollRef.current) {
      clearInterval(trainingPollRef.current);
      trainingPollRef.current = null;
    }

    trainingPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/train/jobs/${jobId}`);
        if (!res.ok) throw new Error(await apiErrorToMessage(res));

        const job = await res.json();

        setTrainingJobId(job.job_id || jobId);
        setTrainingProgress(
          Math.max(0, Math.min(100, Number(job.progress || 0)))
        );

        if (job.status === "queued" || job.status === "running") {
          setTrainStatus("loading");
          setTrainMsg(job.message || "Training in progress...");
          return;
        }

        if (job.status === "completed") {
          clearInterval(trainingPollRef.current);
          trainingPollRef.current = null;

          const activeJob = loadActiveTrainingJob();
          const countryForResult = job.country || activeJob?.country || apiCountry;
          const diseaseForResult = job.threat || activeJob?.disease || apiDisease;
          const metadata = {
            datasetId: activeJob?.datasetId || buildCurrentTrainingMetadata().datasetId,
            datasetSource:
              activeJob?.datasetSource || buildCurrentTrainingMetadata().datasetSource,
            trainingSource:
              activeJob?.trainingSource || buildCurrentTrainingMetadata().trainingSource,
          };

          clearActiveTrainingJob();

          saveLastTrainingResult({
            jobId: job.job_id || jobId,
            country: countryForResult,
            disease: diseaseForResult,
            status: "completed",
            completedAt: new Date().toISOString(),
            message: job.message || "Training completed successfully.",
            ...metadata,
          });

          setTrainStatus("success");
          setTrainMsg(job.message || "Training completed successfully.");
          setTrainingProgress(100);
          setCanAdoptPredict(true);
          return;
        }

        if (job.status === "failed" || job.status === "cancelled") {
          clearInterval(trainingPollRef.current);
          trainingPollRef.current = null;

          clearActiveTrainingJob();

          const msg =
            job.status === "cancelled"
              ? (job.message || "Training cancelled.")
              : (job.error || job.message || "Training failed.");

          saveLastTrainingResult({
            jobId: job.job_id || jobId,
            country: job.country || apiCountry,
            disease: job.threat || apiDisease,
            status: job.status,
            completedAt: new Date().toISOString(),
            message: msg,
            ...buildCurrentTrainingMetadata(),
          });

          setTrainStatus("error");
          setTrainMsg(msg);
          return;
        }
      } catch (e) {
        clearInterval(trainingPollRef.current);
        trainingPollRef.current = null;

        clearActiveTrainingJob();

        saveLastTrainingResult({
          jobId,
          country: apiCountry,
          disease: apiDisease,
          status: "failed",
          completedAt: new Date().toISOString(),
          message: String(e?.message || e || "Failed to track training job."),
          ...buildCurrentTrainingMetadata(),
        });

        setTrainStatus("error");
        setTrainMsg(String(e?.message || e || "Failed to track training job."));
      }
    }, 2500);
  };

  const runTraining = async () => {
    if (!supportedDatasetCombination) {
      setTrainStatus("error");
      setTrainMsg(unsupportedDatasetMessage);
      return;
    }

    if (trainAbortRef.current) {
      try {
        trainAbortRef.current.abort();
      } catch {}
    }
    const controller = new AbortController();
    trainAbortRef.current = controller;

    setTrainStatus("loading");
    setTrainMsg(
      trainMode === "upload"
        ? "Preparing your data for training..."
        : "Getting everything ready for training..."
    );
    setTrainingJobId(null);
    setTrainingProgress(1);
    clearLastTrainingResult();

    try {
      if (trainMode === "ready") {
        if (!ENABLE_LEGACY_LOCAL_TRAINING && (apiDisease === "Plague" || apiDisease === "TB" || apiDisease === "DiseaseX")) {
          const readyTrainingJob = getReadyTrainingJobEndpoint(apiDisease);

          if (!readyTrainingJob.available || !readyTrainingJob.backendEndpoint) {
            throw new Error(
              `Ready dataset training is not available yet for disease: ${apiDisease}`
            );
          }

          const res = await fetch(`${API_BASE}${readyTrainingJob.backendEndpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              country: apiCountry,
              threat: apiDisease,
              data_format: "json",
              data_source: null,
              json_data: null,
            }),
            signal: controller.signal,
          });

          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          const data = await res.json();
          if (!data?.job_id) throw new Error("Training job started without job_id.");

          setTrainingJobId(data.job_id);
          setTrainStatus("loading");
          setTrainMsg("Your data are ready. Training is starting...");
          setTrainingProgress(1);

          saveActiveTrainingJob({
            jobId: data.job_id,
            country: apiCountry,
            disease: apiDisease,
            trainMode,
            datasetId: "IDL",
            datasetSource: "ready",
            trainingSource: "idl",
            startedAt: new Date().toISOString(),
          });

          startTrainingPolling(data.job_id);
          return;
        }
        if (apiDisease !== "DiseaseX") {
          if (!ENABLE_LEGACY_LOCAL_TRAINING) {
            if (apiDisease !== "TB" && !selectedTrainFile) {
              throw new Error("Select a training dataset first.");
            }

            const readyTrainingJob = getReadyTrainingJobEndpoint(apiDisease);
            if (!readyTrainingJob.available || !readyTrainingJob.backendEndpoint) {
              throw new Error(
                `Ready dataset training is not available yet for disease: ${apiDisease}`
              );
            }

            const res = await fetch(`${API_BASE}${readyTrainingJob.backendEndpoint}`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                country: apiCountry,
                threat: apiDisease,
                data_format: "json",
                data_source: null,
                json_data: null,
              }),
              signal: controller.signal,
            });

            if (!res.ok) throw new Error(await apiErrorToMessage(res));

            const data = await res.json();
            if (!data?.job_id) throw new Error("Training job started without job_id.");

            setTrainingJobId(data.job_id);
            setTrainStatus("loading");
            setTrainMsg("Your data are ready. Training is starting...");
            setTrainingProgress(1);

            saveActiveTrainingJob({
              jobId: data.job_id,
              country: apiCountry,
              disease: apiDisease,
              trainMode,
              datasetId: apiDisease === "TB" ? "IDL" : selectedTrainFile || null,
              datasetSource: "ready",
              trainingSource: apiDisease === "TB" ? "idl" : "ready",
              startedAt: new Date().toISOString(),
            });

            startTrainingPolling(data.job_id);
            return;
          }

          if (apiDisease === "Plague" && plagueTrainSource === "idl") {
            const res = await fetch(`${API_BASE}/train/jobs/plague/idl`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                country: apiCountry,
                threat: "Plague",
              }),
              signal: controller.signal,
            });

            if (!res.ok) throw new Error(await apiErrorToMessage(res));

            const data = await res.json();
            if (!data?.job_id) throw new Error("Training job started without job_id.");

            setTrainingJobId(data.job_id);
            setTrainStatus("loading");
            setTrainMsg("Your data are ready. Training is starting...");
            setTrainingProgress(1);

            saveActiveTrainingJob({
              jobId: data.job_id,
              country: apiCountry,
              disease: "Plague",
              trainMode,
              datasetId: "IDL",
              datasetSource: "ready",
              trainingSource: "idl",
              startedAt: new Date().toISOString(),
            });

            startTrainingPolling(data.job_id);
            return;
          }

          const fileToUse = selectedTrainFile;
          if (!fileToUse) throw new Error("Select a training dataset first.");

          const payload = {
            country: apiCountry,
            threat: apiDisease,
            data_format: "csv",
            data_source: `./Datasets/${fileToUse}`,
            json_data: null,
          };

          const endpoint =
            apiDisease === "TB"
              ? `${API_BASE}/train/jobs/tb`
              : `${API_BASE}/train/jobs/plague`;

          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });

          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          const data = await res.json();
          if (!data?.job_id) throw new Error("Training job started without job_id.");

          setTrainingJobId(data.job_id);
          setTrainStatus("loading");
          setTrainMsg("Your data are ready. Training is starting...");

          saveActiveTrainingJob({
            jobId: data.job_id,
            country: apiCountry,
            disease: apiDisease,
            trainMode,
            datasetId:
              apiDisease === "DiseaseX"
                ? `${selectedGtrends || ""}|${selectedEpd || ""}|${selectedMobility || ""}`
                : selectedTrainFile || null,
            datasetSource: "ready",
            trainingSource: apiDisease === "Plague" ? plagueTrainSource : "local",
            startedAt: new Date().toISOString(),
          });

          startTrainingPolling(data.job_id);
          return;
        }

        if (!ENABLE_LEGACY_LOCAL_TRAINING) {
          const readyTrainingJob = getReadyTrainingJobEndpoint(apiDisease);
          if (!readyTrainingJob.available || !readyTrainingJob.backendEndpoint) {
            throw new Error(
              `Ready dataset training is not available yet for disease: ${apiDisease}`
            );
          }
        }

        if (!selectedGtrends || !selectedEpd || !selectedMobility) {
          throw new Error("Select GTrends, EPD and Mobility datasets first.");
        }

        const payload = {
          country: apiCountry,
          threat: "DiseaseX",

          gtrends_data_format: "csv",
          gtrends_data_source: `./Datasets/${selectedGtrends}`,
          gtrends_json_data: null,

          epd_data_format: "csv",
          epd_data_source: `./Datasets/${selectedEpd}`,
          epd_json_data: null,

          mobility_data_format: "csv",
          mobility_data_source: `./Datasets/${selectedMobility}`,
          mobility_json_data: null,
        };

        const res = await fetch(`${API_BASE}/train/jobs/diseasex`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(await apiErrorToMessage(res));

        const data = await res.json();
        if (!data?.job_id) throw new Error("Training job started without job_id.");

        setTrainingJobId(data.job_id);
        setTrainStatus("loading");
        setTrainMsg("Your data are ready. Training is starting...");

        saveActiveTrainingJob({
          jobId: data.job_id,
          country: apiCountry,
          disease: apiDisease,
          trainMode,
          datasetId: `${selectedGtrends || ""}|${selectedEpd || ""}|${selectedMobility || ""}`,
          datasetSource: "ready",
          trainingSource: apiDisease === "Plague" ? plagueTrainSource : "local",
          startedAt: new Date().toISOString(),
        });

        startTrainingPolling(data.job_id);
        return;
      }

      if (trainMode === "upload") {
        if (apiDisease !== "DiseaseX") {
          if (apiDisease === "TB") {
            if (!uploadTrainFile || !uploadTbDemographics || !uploadTbRiskFactors) {
              throw new Error(
                "Upload the time-series, demographics and risk-factor CSV files first."
              );
            }
          } else if (!uploadTrainFile) {
            throw new Error("Upload a training CSV first.");
          }

          let res;
          let trainingSource;

          if (apiDisease === "TB") {
            const formData = new FormData();
            formData.append("country_iso", toTbCountryIso(apiCountry));
            formData.append("time_series_dataset", uploadTrainFile);
            formData.append("demographics_dataset", uploadTbDemographics);
            formData.append("risk_factors_dataset", uploadTbRiskFactors);

            res = await fetch(`${API_BASE}/train/jobs/tb/cdm-upload`, {
              method: "POST",
              body: formData,
              signal: controller.signal,
            });
            trainingSource = "cdm_upload";
          } else {
            const csv = await readCsvFile(uploadTrainFile);
            const rows = csvToObjects(csv);
            const payload = {
              country: apiCountry,
              threat: apiDisease,
              json_data: rows,
              data_source: null,
              data_format: "json",
            };

            res = await fetch(`${API_BASE}/train/jobs/plague`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
              signal: controller.signal,
            });
            trainingSource = plagueTrainSource;
          }

          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          const data = await res.json();
          if (!data?.job_id) throw new Error("Training job started without job_id.");

          setTrainingJobId(data.job_id);
          setTrainStatus("loading");
          setTrainMsg("Your data are ready. Training is starting...");

          saveActiveTrainingJob({
            jobId: data.job_id,
            country: apiCountry,
            disease: apiDisease,
            trainMode,
            datasetId:
              apiDisease === "TB"
                ? `${uploadTrainFile?.name || ""}|${uploadTbDemographics?.name || ""}|${uploadTbRiskFactors?.name || ""}`
                : uploadTrainFile?.name || null,
            datasetSource: "upload",
            trainingSource,
            startedAt: new Date().toISOString(),
          });

          startTrainingPolling(data.job_id);
          return;
        }

        // DiseaseX upload: need 3 files
        if (!uploadGtrends || !uploadEpd || !uploadMobility) {
          throw new Error("Upload GTrends, EPD and Mobility CSVs first.");
        }

        const [csvG, csvE, csvM] = await Promise.all([
          readCsvFile(uploadGtrends),
          readCsvFile(uploadEpd),
          readCsvFile(uploadMobility),
        ]);

        const payload = {
          country: apiCountry,
          threat: "DiseaseX",

          gtrends_json_data: csvToObjects(csvG),
          gtrends_data_source: null,
          gtrends_data_format: "json",

          epd_json_data: csvToObjects(csvE),
          epd_data_source: null,
          epd_data_format: "json",

          mobility_json_data: csvToObjects(csvM),
          mobility_data_source: null,
          mobility_data_format: "json",
        };

        const res = await fetch(`${API_BASE}/train/jobs/diseasex`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(await apiErrorToMessage(res));

        const data = await res.json();
        if (!data?.job_id) throw new Error("Training job started without job_id.");

        setTrainingJobId(data.job_id);
        setTrainStatus("loading");
        setTrainMsg("Your data are ready. Training is starting...");

        saveActiveTrainingJob({
          jobId: data.job_id,
          country: apiCountry,
          disease: apiDisease,
          trainMode,
          datasetId: `${uploadGtrends?.name || ""}|${uploadEpd?.name || ""}|${uploadMobility?.name || ""}`,
          datasetSource: "upload",
          trainingSource: apiDisease === "Plague" ? plagueTrainSource : "local",
          startedAt: new Date().toISOString(),
        });

        startTrainingPolling(data.job_id);
        return;
      }

      throw new Error("Select a training mode first (ready datasets / upload).");
    } catch (e) {
      if (e?.name === "AbortError") {
        setTrainStatus("error");
        setTrainMsg("Training canceled.");
        return;
      }
      setTrainStatus("error");
      setTrainMsg(String(e?.message || e || "Training failed."));
    } finally {
      trainAbortRef.current = null;
    }
  };

  // -----------------------------
  // Predict (Button 3)
  // -----------------------------

const fetchReadyPredictDatasets = async () => {

  let url =
    `${API_BASE}/datasets` +
    `?purpose=predict` +
    `&country=${encodeURIComponent(apiCountry)}` +
    `&threat=${encodeURIComponent(apiDisease)}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(await apiErrorToMessage(res));
  const data = await res.json();

  const filesRaw = Array.isArray(data.files) ? data.files : [];
  const files = _onlyCsv(filesRaw);

  return files.filter((f) =>
    f.toLowerCase().includes("input")
  );
};

const startPredictModeReady = async () => {
  setPredictMenuOpen(false);
  setPredictMode("ready");
  setPredictStatus(null);
  setPredictMsg(null);

  setReadyPredictFiles([]);
  setSelectedPredictFile("");
  setSelectedPredictGtrends("");
  setSelectedPredictEpd("");
  setSelectedPredictMobility("");

  if (apiDisease === "DiseaseX" || apiDisease === "Plague") {
    return;
  }

  try {
    const files = await fetchReadyPredictDatasets();
      setReadyPredictFiles(files);

      if (apiDisease === "TB" || apiDisease === "Plague") {
        setSelectedPredictFile(files[0] || "");
      }

      if (apiDisease === "DiseaseX") {
        const g = _filterByKeyword(files, "gtrends")[0] || "";
        const e = _filterByKeyword(files, "epd")[0] || "";
        const m = _filterByKeyword(files, "mobility")[0] || "";
        setSelectedPredictGtrends(g);
        setSelectedPredictEpd(e);
        setSelectedPredictMobility(m);
      }
    } catch (e) {
      setPredictStatus("error");
      setPredictMsg(String(e?.message || e || "Failed to load input datasets."));
    }
  };

  const startPredictModeUpload = () => {
    setPredictMenuOpen(false);
    setPredictMode("upload");
    setPredictStatus(null);
    setPredictMsg(null);

    setUploadPredictFile(null);
    setUploadPredictGtrends(null);
    setUploadPredictEpd(null);
    setUploadPredictMobility(null);
  };

const waitForPredictionJobCompletion = async (jobId, signal) => {
  const maxAttempts = 360;
  const delayMs = 2000;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("Prediction canceled.", "AbortError");
    }

    const res = await fetch(`${API_BASE}/train/jobs/${jobId}`, { signal });
    if (!res.ok) {
      throw new Error(await apiErrorToMessage(res));
    }

    const job = await res.json();

    if (job.status === "completed") {
      return job;
    }

    if (job.status === "failed") {
      throw new Error(job.message || job.error || "DiseaseX prediction failed.");
    }

    if (job.status === "cancelled") {
      throw new Error("DiseaseX prediction was cancelled.");
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  throw new Error("DiseaseX prediction did not complete in time.");
};

  const runPredict = async () => {
    if (!canAdoptPredict) return;

    if (predictAbortRef.current) {
      try {
        predictAbortRef.current.abort();
      } catch {}
    }
    const controller = new AbortController();
    predictAbortRef.current = controller;

    setPredictStatus("loading");
    setPredictMsg(
      predictMode === "upload"
        ? "Preparing your data for prediction..."
        : "Preparing your prediction..."
    );

    try {
      const endpoint =
        apiDisease === "TB"
          ? `${API_BASE}/predict_TB_models`
          : apiDisease === "Plague"
          ? `${API_BASE}/predict_plague_models`
          : `${API_BASE}/predict_diseaseX_models`;

      if (predictMode === "ready") {
        if (apiDisease === "Plague") {
          const res = await fetch(`${API_BASE}/predict/plague/idl`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({country: apiCountry, threat: "Plague", data_format: "json", data_source: null, json_data: null}),
            signal: controller.signal,
          });
          if (!res.ok) throw new Error(await apiErrorToMessage(res));
          await res.json();
          setPredictStatus("success");
          setPredictMsg("Your prediction is ready. Input data were fetched from IDL.");
          return;
        }
        if (apiDisease !== "DiseaseX") {
          const fileToUse = selectedPredictFile;
          if (!fileToUse) throw new Error("Select an input dataset first.");

          const payload = {
            country: apiCountry,
            threat: apiDisease,
            data_format: "csv",
            data_source: `./Datasets/${fileToUse}`,
            json_data: null,
          };

          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });

          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          await res.json().catch(() => null); 
          setPredictStatus("success");
          setPredictMsg("Your prediction is ready.");
          return;
        }

        // DiseaseX 
        const res = await fetch(`${API_BASE}/predict/jobs/diseasex/idl`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            country: apiCountry,
            threat: "DiseaseX",
            data_format: "json",
            data_source: null,
            json_data: null,
          }),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(await apiErrorToMessage(res));

        const data = await res.json();
        if (!data?.job_id) {
          throw new Error("DiseaseX prediction job started without job_id.");
        }

        setPredictMsg("DiseaseX prediction started...");
        await waitForPredictionJobCompletion(data.job_id, controller.signal);

        setPredictStatus("success");
        setPredictMsg("Your prediction is ready.");
        return;
      }

      // UPLOAD MODE
      if (predictMode === "upload") {
        if (apiDisease !== "DiseaseX") {
          if (!uploadPredictFile) throw new Error("Upload an input CSV first.");

          let res;

          if (apiDisease === "TB") {
            const formData = new FormData();
            formData.append("country_iso", toTbCountryIso(apiCountry));
            formData.append("dataset", uploadPredictFile);

            res = await fetch(`${API_BASE}/predict_TB_models/cdm-upload`, {
              method: "POST",
              body: formData,
              signal: controller.signal,
            });
          } else {
            const csv = await readCsvFile(uploadPredictFile);
            const rows = csvToObjects(csv);
            const payload = {
              country: apiCountry,
              threat: apiDisease,
              json_data: rows,
              data_source: null,
              data_format: "json",
            };

            res = await fetch(endpoint, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
              signal: controller.signal,
            });
          }

          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          await res.json().catch(() => null); 
          setPredictStatus("success");
          setPredictMsg("Your prediction is ready.");
          return;
        }

        // DiseaseX upload 
        if (!uploadPredictGtrends || !uploadPredictEpd || !uploadPredictMobility) {
          throw new Error("Upload GTrends, EPD and Mobility input CSVs first.");
        }

        const [csvG, csvE, csvM] = await Promise.all([
          readCsvFile(uploadPredictGtrends),
          readCsvFile(uploadPredictEpd),
          readCsvFile(uploadPredictMobility),
        ]);

        const payload = {
          country: apiCountry,
          threat: "DiseaseX",

          gtrends_json_data: csvToObjects(csvG),
          gtrends_data_source: null,
          gtrends_data_format: "json",

          epd_json_data: csvToObjects(csvE),
          epd_data_source: null,
          epd_data_format: "json",

          mobility_json_data: csvToObjects(csvM),
          mobility_data_source: null,
          mobility_data_format: "json",
        };

        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(await apiErrorToMessage(res));

        await res.json().catch(() => null); 
        setPredictStatus("success");
        setPredictMsg("Your prediction is ready.");
        return;
      }

      throw new Error("Select a prediction mode first (ready datasets / upload).");
    } catch (e) {
      if (e?.name === "AbortError") {
        setPredictStatus("error");
        setPredictMsg("Prediction canceled.");
        return;
      }
      setPredictStatus("error");
      setPredictMsg(String(e?.message || e || "Prediction failed."));
    } finally {
      predictAbortRef.current = null;
    }
  };

  const handleAdoptPredict = () => {
    if (!canAdoptPredict) return;
    setPredictMenuOpen((v) => !v);
  };

  const handleBackClick = () => {
    cancelPredict();

    setVizMode(null);
    setVizMenuOpen(false);
    setAssets([]);
    setAssetsError(null);
    setAssetsLoading(false);

    setTrainMenuOpen(false);
    setPredictMenuOpen(false);
    setPredictMode(null);
    setPredictStatus(null);
    setPredictMsg(null);

    refreshPredictionAvailability();
    setUrlParams({ view: null });
  };

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!vizMenuOpen) return;
      if (!vizWrapRef.current) return;
      if (!vizWrapRef.current.contains(e.target)) setVizMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [vizMenuOpen]);


  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!trainMenuOpen) return;
      if (!trainWrapRef.current) return;
      if (!trainWrapRef.current.contains(e.target)) setTrainMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [trainMenuOpen]);

  useEffect(() => {
    return () => {
      if (trainingPollRef.current) {
        clearInterval(trainingPollRef.current);
        trainingPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const activeJob = loadActiveTrainingJob();

    if (
      activeJob?.jobId &&
      activeJob.country === apiCountry &&
      activeJob.disease === apiDisease
    ) {
      setTrainingJobId((prev) => prev || activeJob.jobId);
      setTrainStatus("loading");
      setTrainMsg("Reconnecting to training job...");
      setTrainingProgress((prev) => (prev > 0 ? prev : 1));

      if (!trainMode) {
        setTrainMode(activeJob.trainMode || "ready");
      }

      if (!trainingPollRef.current) {
        startTrainingPolling(activeJob.jobId);
      }
      return;
    }

    const lastResult = loadLastTrainingResult();

    if (
      lastResult &&
      lastResult.country === apiCountry &&
      lastResult.disease === apiDisease
    ) {
      setTrainingJobId(lastResult.jobId || null);
      setTrainMode((prev) => prev || lastResult.datasetSource || "ready");
      setTrainingProgress(lastResult.status === "completed" ? 100 : 0);

      if (lastResult.status === "completed") {
        setTrainStatus("success");
        setTrainMsg(
          lastResult.message || "Training completed successfully."
        );
        setCanAdoptPredict(true);
      } else {
        setTrainStatus("error");
        setTrainMsg(lastResult.message || "Training did not complete.");
      }
      return;
    }

    setTrainingJobId(null);
    setTrainingProgress(0);
    setTrainStatus(null);
    setTrainMsg(null);
  }, [apiCountry, apiDisease]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!predictMenuOpen) return;
      if (!predictWrapRef.current) return;
      if (!predictWrapRef.current.contains(e.target)) setPredictMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [predictMenuOpen]);

  useEffect(() => {
    refreshPredictionAvailability();
  }, [apiCountry, apiDisease]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!historicalMenuOpen) return;
      if (!historicalWrapRef.current) return;
      if (!historicalWrapRef.current.contains(e.target)) {
        setHistoricalMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [historicalMenuOpen]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);

    const urlCountry = sp.get("country");
    const urlDisease = sp.get("disease");
    const urlView = sp.get("view");

    if (urlCountry) setCountry(urlCountry);
    if (urlDisease) setDisease(urlDisease);

    if (urlView === "predictions") {
      setVizMode("predictions");

      const effectiveCountry = urlCountry || country;
      const effectiveDisease = urlDisease || disease;

      const effectiveApiCountry = toApiCountry(effectiveCountry);
      const effectiveApiDisease = toApiDiseaseToken(effectiveDisease);

      (async () => {
        setAssetsLoading(true);
        setAssetsError(null);
        setAssets([]);

        try {
          const url =
            `${API_BASE}/visualizations/biological` +
            `?country=${encodeURIComponent(effectiveApiCountry)}` +
            `&disease=${encodeURIComponent(effectiveApiDisease)}` +
            `&prefer=html`;

          const res = await fetch(url);
          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          const data = await res.json();
          const all = Array.isArray(data.assets) ? data.assets : [];
          const filtered = all.filter((a) => a && (a.kind === "map" || a.kind === "chart"));

          setAssets(filtered);
        } catch (e) {
          setAssetsError(String(e?.message || e || "Failed to load prediction outputs."));
        } finally {
          setAssetsLoading(false);
        }
      })();

      setUrlParams({
        country: urlCountry || country,
        disease: urlDisease || disease,
        view: "predictions",
      });

      return; 
    }

    if (urlView === "historical") {
      setVizMode("historical");

      setUrlParams({
        country: urlCountry || country,
        disease: urlDisease || disease,
        view: "historical",
      });

      return;
    }

    if (urlView === "map" || urlView === "chart") {
      setVizMode(urlView);

      const effectiveCountry = urlCountry || country;
      const effectiveDisease = urlDisease || disease;

      const effectiveApiCountry = toApiCountry(effectiveCountry);
      const effectiveApiDisease = toApiDiseaseToken(effectiveDisease);

      (async () => {
        setAssetsLoading(true);
        setAssetsError(null);
        setAssets([]);

        try {
          const url =
            `${API_BASE}/visualizations/biological` +
            `?country=${encodeURIComponent(effectiveApiCountry)}` +
            `&disease=${encodeURIComponent(effectiveApiDisease)}` +
            `&prefer=html`;

          const res = await fetch(url);
          if (!res.ok) throw new Error(await apiErrorToMessage(res));

          const data = await res.json();
          const all = Array.isArray(data.assets) ? data.assets : [];

          const filtered = all.filter((a) => {
            if (!a || !a.kind) return false;
            return urlView === "map" ? a.kind === "map" : a.kind === "chart";
          });

          setAssets(filtered);
        } catch (e) {
          setAssetsError(String(e?.message || e || "Failed to load visualizations."));
        } finally {
          setAssetsLoading(false);
        }
      })();
    }

    if (urlCountry || urlDisease || urlView) {
      setUrlParams({
        country: urlCountry || country,
        disease: urlDisease || disease,
        view:
          urlView === "map" ||
          urlView === "chart" ||
          urlView === "predictions" ||
          urlView === "historical"
            ? urlView
            : null,
      });
    }
  }, []);


  const Filters = (
    <div className="ds-card bp-filters">
      <div className="bp-filters-left">
        <div className="bp-control">
          <label>Country</label>
          <select
            value={country}
            onChange={(e) => {
              const nextCountry = e.target.value;

              // cancel any running requests
              cancelTraining();
              cancelPredict();

              setCountry(nextCountry);
              setVizMode(null);
              setVizMenuOpen(false);
              setAssets([]);
              setAssetsError(null);

              hardResetAllAiUi();
              setCanAdoptPredict(false);

              setUrlParams({ country: nextCountry, disease, view: null });
            }}
          >
            <option value="UK">UK</option>
            <option value="Ukraine">Ukraine</option>
            <option value="Greece">Greece</option>
            <option value="Italy">Italy</option>
            <option value="Spain">Spain</option>
            <option value="Ireland">Ireland</option>
            <option value="Israel">Israel</option>
            <option value="Sweden">Sweden</option>
            <option value="Switzerland">Switzerland</option>
          </select>
        </div>

        <div className="bp-control">
          <label>Disease</label>
          <select
            value={disease}
            onChange={(e) => {
              const nextDisease = e.target.value;

              // cancel any running requests
              cancelTraining();
              cancelPredict();

              setDisease(nextDisease);
              setVizMode(null);
              setVizMenuOpen(false);
              setAssets([]);
              setAssetsError(null);

              hardResetAllAiUi();
              setCanAdoptPredict(false);

              setUrlParams({ country, disease: nextDisease, view: null });
            }}
          >
            <option value="DiseaseX">Disease X</option>
            <option value="Tuberculosis">Tuberculosis</option>
            <option value="Plague">Plague</option>
          </select>
        </div>
      </div>

      <div className="bp-filters-actions">
        {vizMode === null ? (
          <>
            {/* Visualization dropdown */}
            <div className="bp-viz-wrap" ref={vizWrapRef}>
              <button
                className="ds-btn"
                onClick={() => setVizMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={vizMenuOpen}
                type="button"
              >
                Visualization
              </button>

              {vizMenuOpen && (
                <div className="bp-viz-menu" role="menu">
                  <button
                    className="bp-viz-item"
                    role="menuitem"
                    type="button"
                    onClick={() => selectVizMode("map")}
                  >
                    Map
                  </button>
                  <button
                    className="bp-viz-item"
                    role="menuitem"
                    type="button"
                    onClick={() => selectVizMode("chart")}
                  >
                    Chart
                  </button>
                </div>
              )}
            </div>

            {/* Create New AI Models dropdown */}
            <div className="bp-viz-wrap" ref={trainWrapRef}>
              <button
                className="ds-btn"
                onClick={openTrainMenu}
                aria-haspopup="menu"
                aria-expanded={trainMenuOpen}
                type="button"
              >
                Create New AI Models
              </button>

              {trainMenuOpen && (
                <div className="bp-viz-menu" role="menu">
                  <button
                    className="bp-viz-item"
                    role="menuitem"
                    type="button"
                    onClick={startTrainModeReady}
                  >
                    Use ready datasets
                  </button>
                {apiDisease === "TB" && (
                  <button
                    className="bp-viz-item"
                    role="menuitem"
                    type="button"
                    onClick={startTrainModeUpload}
                  >
                    Upload new dataset
                  </button>
                )}
                </div>
              )}
            </div>

            {/* Adopt AI Models - Predict dropdown */}
            <div className="bp-viz-wrap" ref={predictWrapRef}>
              <button
                className={`ds-btn ${!canAdoptPredict ? "ds-btn--disabled" : ""}`}
                onClick={handleAdoptPredict}
                disabled={!canAdoptPredict}
                aria-disabled={!canAdoptPredict}
                aria-haspopup="menu"
                aria-expanded={predictMenuOpen}
                type="button"
                title={
                  canAdoptPredict
                    ? "Adopt AI Models - Predict"
                    : "Please first press Create New AI Models and wait for training"
                }
              >
                Adopt AI Models - Predict
              </button>

              {predictMenuOpen && canAdoptPredict && (
                <div className="bp-viz-menu" role="menu">
                  <button
                    className="bp-viz-item"
                    role="menuitem"
                    type="button"
                    onClick={startPredictModeReady}
                  >
                    Use ready datasets
                  </button>
                    {apiDisease !== "DiseaseX" && (
                      <button
                        className="bp-viz-item"
                        role="menuitem"
                        type="button"
                        onClick={startPredictModeUpload}
                      >
                        Upload new dataset
                      </button>
                    )}
                </div>
              )}
            </div>
            <div className="bp-historical-slot">
              <div className="bp-viz-wrap" ref={historicalWrapRef}>
                <button
                  className="ds-btn bp-historical-trigger"
                  onClick={() => setHistoricalMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={historicalMenuOpen}
                  type="button"
                  title="Display historical data"
                >
                  Historical Data
                </button>

                {historicalMenuOpen && (
                  <div className="bp-viz-menu bp-historical-menu" role="menu">
                    {HISTORICAL_DISEASE_OPTIONS.map((option) => (
                      <button
                        key={option}
                        className="bp-viz-item"
                        role="menuitem"
                        type="button"
                        onClick={() => openHistoricalView(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>



          </>
        ) : (
          <button className="ds-btn" onClick={handleBackClick}>
            Back
          </button>
        )}
      </div>
    </div>
  );

  const Placeholder = (
    <div className="ds-card bp-placeholder">Select Country and Disease</div>
  );

  const EmptyState = (
    <div className="ds-col-12 ds-card bp-placeholder">
      {assetsLoading
        ? "Loading..."
        : assetsError
        ? assetsError
        : vizMode === "map"
        ? "No map available for this selection."
        : "No charts available for this selection."}
    </div>
  );

  const TrainingPanel = trainMode ? (
    <div className="ds-card bp-placeholder" style={{ textAlign: "left" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <strong>
          Training:{" "}
          {trainMode === "ready" ? "Use ready datasets" : "Upload new dataset"}
        </strong>
          <button
            className="ds-btn"
            type="button"
            onClick={() => {
              setTrainMode(null);
            }}
          >
            Close
          </button>
      </div>

      {trainStatus === "loading" && (
        <div className="bp-training-progress-card">
          <div className="bp-training-progress-header">
            <div className="bp-training-progress-title">
              <span className="bp-spinner" aria-hidden="true" />
              <span>Training {trainingDiseaseLabel} AI models</span>
            </div>

            <button
              className="ds-btn"
              type="button"
              onClick={requestTrainingCancel}
              title="Cancel training"
            >
              Cancel training
            </button>
          </div>

          <div className="bp-training-progress-message" role="status" aria-live="polite">
            {trainMsg || "Preparing training job..."}
          </div>

          <div className="bp-training-progress-meta">
            <span>Progress</span>
            <strong>{trainingProgress}%</strong>
          </div>

          <div className="bp-training-progress-track" aria-hidden="true">
            <div
              className="bp-training-progress-bar"
              style={{ width: `${trainingProgress}%` }}
            />
          </div>
        </div>
      )}

      {trainStatus === "success" && <div style={{ marginTop: 10 }}>✅ {trainMsg}</div>}
      {trainStatus === "error" && trainMsg !== unsupportedDatasetMessage && (
        <div style={{ marginTop: 10 }}>❌ {trainMsg}</div>
      )}

      {!supportedDatasetCombination && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: "var(--ui-error-bg, rgba(239, 68, 68, 0.12))",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            color: "var(--ui-error, #fecaca)",
            fontSize: "0.8125rem",
          }}
        >
          {unsupportedDatasetMessage}
        </div>
      )}

      {trainMode === "ready" && (
        <div style={{ marginTop: 12 }}>
          {apiDisease === "Plague" || apiDisease === "TB" || apiDisease === "DiseaseX" ? (
            <div className="bp-training-ready-card">
              <div className="bp-training-ready-line">
                {trainingDataSourceMessage}
              </div>

              <div className="bp-training-ready-line bp-training-ready-line-muted">
                The model training will continue in the background.
              </div>
            </div>
          ) : (
            <>
              <label>Training dataset</label>
              <select
                value={selectedTrainFile}
                onChange={(e) => setSelectedTrainFile(e.target.value)}
                disabled={!supportedDatasetCombination}
                style={{ width: "100%", marginTop: 6 }}
              >
                <option value="">
                  {readyFiles.length ? "Select a dataset" : "No datasets found"}
                </option>
                {readyFiles.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      {trainMode === "upload" && (
        <div style={{ marginTop: 12 }}>
          {apiDisease === "TB" ? (
            <div className="bp-tb-upload-grid">
              <div className="bp-tb-upload-field">
                <label>TB time-series training dataset (CDM CSV)</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setUploadTrainFile(e.target.files?.[0] || null)}
                  disabled={!supportedDatasetCombination}
                />
              </div>

              <div className="bp-tb-upload-field">
                <label>TB demographics dataset (CDM CSV)</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) =>
                    setUploadTbDemographics(e.target.files?.[0] || null)
                  }
                  disabled={!supportedDatasetCombination}
                />
              </div>

              <div className="bp-tb-upload-field">
                <label>TB risk-factor dataset (CDM CSV)</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) =>
                    setUploadTbRiskFactors(e.target.files?.[0] || null)
                  }
                  disabled={!supportedDatasetCombination}
                />
              </div>

              <div className="bp-tb-upload-help">
                Select all three files from the same country before starting training.
              </div>
            </div>
          ) : apiDisease !== "DiseaseX" ? (
            <>
              <label>Upload training dataset</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadTrainFile(e.target.files?.[0] || null)}
                disabled={!supportedDatasetCombination}
                style={{ display: "block", marginTop: 6 }}
              />
            </>
          ) : (
            <>
              <label>Upload GTrends training dataset</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadGtrends(e.target.files?.[0] || null)}
                disabled={!supportedDatasetCombination}
                style={{ display: "block", marginTop: 6 }}
              />

              <label style={{ marginTop: 10, display: "block" }}>Upload EPD training dataset</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadEpd(e.target.files?.[0] || null)}
                disabled={!supportedDatasetCombination}
                style={{ display: "block", marginTop: 6 }}
              />

              <label style={{ marginTop: 10, display: "block" }}>Upload Mobility training dataset</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadMobility(e.target.files?.[0] || null)}
                disabled={!supportedDatasetCombination}
                style={{ display: "block", marginTop: 6 }}
              />
            </>
          )}
        </div>
      )}

      <div className="bp-training-actions">
        <button
          className={`ds-btn bp-training-start-btn ${
            trainStatus === "loading" || !supportedDatasetCombination
              ? "ds-btn--disabled"
              : ""
          }`}
          type="button"
          onClick={runTraining}
          disabled={trainStatus === "loading" || !supportedDatasetCombination}
          aria-disabled={trainStatus === "loading" || !supportedDatasetCombination}
          title={
            trainStatus === "loading"
              ? "Training is in progress"
              : "Train models"
          }
        >
          Start training
        </button>
      </div>
    </div>
  ) : null;

  const PredictPanel = predictMode ? (
    <div className="ds-card bp-placeholder" style={{ textAlign: "left" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <strong>
          Predict: {predictMode === "ready" ? "Use ready datasets" : "Upload new dataset"}
        </strong>
        <button
          className="ds-btn"
          type="button"
          onClick={() => {
            cancelPredict();
            setPredictMode(null);
            setPredictStatus(null);
            setPredictMsg(null);
          }}
        >
          Close
        </button>
      </div>

      {predictStatus === "loading" && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <span className="bp-spinner" aria-hidden="true" />
          <div>Predicting</div>

          <button
            className="ds-btn"
            type="button"
            onClick={cancelPredict}
            style={{ marginLeft: "auto" }}
            title="Cancel prediction"
          >
            Cancel
          </button>
        </div>
      )}

      {predictStatus === "success" && (
        <div style={{ marginTop: 10 }}>
          <div style={{ whiteSpace: "pre-wrap" }}>✅ {predictMsg}</div>

          <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
            <button className="ds-btn" type="button" onClick={openPredictionsView}>
              Display predictions
            </button>
          </div>
        </div>
      )}


      {predictStatus === "error" && (
        <div style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>❌ {predictMsg}</div>
      )}

      {predictMode === "ready" && (
        <div style={{ marginTop: 12 }}>
          {apiDisease === "DiseaseX" || apiDisease === "Plague" ? (
            <div style={{ marginTop: 6, fontSize: "0.8125rem", opacity: 0.85, lineHeight: 1.5 }}>
              Prediction data for {apiDisease} in {apiCountry} are fetched automatically from the central data repository (IDL).
            </div>
          ) : (
            <>
              <label>Input dataset (CSV)</label>
              <select
                value={selectedPredictFile}
                onChange={(e) => setSelectedPredictFile(e.target.value)}
                style={{ width: "100%", marginTop: 6 }}
              >
                <option value="">
                  {readyPredictFiles.length
                    ? "Select an input dataset..."
                    : "No input datasets found"}
                </option>
                {readyPredictFiles.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      {predictMode === "upload" && (
        <div style={{ marginTop: 12 }}>
          {apiDisease !== "DiseaseX" ? (
            <>
              <label>
                {apiDisease === "TB"
                  ? "Upload TB time-series input dataset (CDM CSV, at least 12 months)"
                  : "Upload input CSV (e.g., 12 rows)"}
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadPredictFile(e.target.files?.[0] || null)}
                style={{ display: "block", marginTop: 6 }}
              />
            </>
          ) : (
            <>
              <label>Upload GTrends input CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadPredictGtrends(e.target.files?.[0] || null)}
                style={{ display: "block", marginTop: 6 }}
              />

              <label style={{ marginTop: 10, display: "block" }}>Upload EPD input CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadPredictEpd(e.target.files?.[0] || null)}
                style={{ display: "block", marginTop: 6 }}
              />

              <label style={{ marginTop: 10, display: "block" }}>Upload Mobility input CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadPredictMobility(e.target.files?.[0] || null)}
                style={{ display: "block", marginTop: 6 }}
              />
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
        <button
          className="ds-btn"
          type="button"
          onClick={runPredict}
          disabled={predictStatus === "loading" || !canAdoptPredict}
          title={!canAdoptPredict ? "Train models first." : "Run prediction"}
        >
          Start prediction
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="pageContainer">
      <div className="ds-header">
        <h1 className="ds-title">Biological Agents</h1>
      </div>

      {vizMode === null && (
        <div className="bp-stack">
          {Placeholder}
          {Filters}
          {TrainingPanel}
          {PredictPanel}
        </div>
      )}

      {vizMode !== null && (
        <div className="bp-stack">
          {Filters}
          {TrainingPanel}
          {PredictPanel}

          {/* MAP MODE */}
          {vizMode === "map" && (
            <div className="ds-grid bp-charts">
              {assets.length === 0 ? (
                EmptyState
              ) : (
                assets.map((a) => (
                  <div key={a.filename} className="ds-col-12 ds-card bp-chart">
                    <iframe
                      title={a.title}
                      src={`${API_BASE}${a.url}`}
                      className="bp-standard-chart-frame"
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {/* CHART MODE */}
          {vizMode === "chart" && (
            <div className="ds-grid bp-charts">
              {assets.length === 0 ? (
                EmptyState
              ) : (
                assets.map((a) => (
                  <div key={a.filename} className="ds-col-12 ds-card bp-chart">
                    <iframe
                      title={a.title}
                      src={`${API_BASE}${a.url}`}
                      className="bp-standard-chart-frame"
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {vizMode === "predictions" && (
            <div className="ds-grid bp-charts">
              {assets.length === 0 ? (
                <div className="ds-col-12 ds-card bp-placeholder">
                  {assetsLoading
                    ? "Loading..."
                    : assetsError
                    ? assetsError
                    : "No prediction outputs available for this selection."}
                </div>
              ) : (
                assets.map((a) => (
                  <div
                    key={a.filename}
                    className={`ds-col-12 ds-card bp-chart ${
                      a.kind === "map" ? "bp-prediction-map-card" : "bp-prediction-chart-card"
                    }`}
                  >
                    <div className="bp-chart-title">
                      <span>
                        {a.kind === "map" ? "Map" : "Chart"} — {a.title}
                      </span>

                      <InfoTooltip message={getVisualizationInfoMessage(a, apiCountry)} />
                    </div>

                    <iframe
                      title={a.title}
                      src={`${API_BASE}${a.url}`}
                      className={
                        a.kind === "map"
                          ? "bp-prediction-map-frame"
                          : "bp-prediction-chart-frame"
                      }
                      style={{ border: "0", borderRadius: "12px" }}
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {vizMode === "historical" && (
            <div className="ds-grid bp-charts">
              <div className="ds-col-12 ds-card bp-chart bp-historical-chart">
                <div className="bp-historical-title">
                  Historical Data — {historicalDisease}
                </div>

                {showHistoricalIframe ? (
                  <iframe
                    title={`Historical Data - ${historicalDisease}`}
                    src={historicalDashboardUrl}
                    width="100%"
                    height="600"
                    style={{ border: "0", borderRadius: "12px" }}
                    loading="lazy"
                  />
                ) : (
                  <div className="bp-no-historical-data">
                    No historical data to display.
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
